const gettingStartedContent = {
    title: "Getting Started with Sortio",
    description: "Learn how to use Sortio to organize your files with AI-powered sorting.",
    sections: [
      {
        id: "installation",
        title: "Installing Sortio",
        content: `
          <div class="flex flex-col md:flex-row gap-8 mb-10">
            <div class="flex-1">
              <p class="text-[#1d1d1f] text-lg">Sortio is available for macOS and Windows. Follow these steps to get started:</p>
              
              <div class="mt-6 bg-white/80 border border-[#e0e0e5] rounded-xl p-6 backdrop-blur-sm">
                <ol class="space-y-5">
                  <li class="flex items-center">
                    <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-4 shrink-0">
                      <span class="text-white font-medium text-sm">1</span>
                    </div>
                    <span class="text-[#1d1d1f]">Download Sortio from our <a href="/download" class="text-[#06c] hover:text-[#0077e6] transition-colors font-medium">download page</a></span>
                  </li>
                  <li class="flex items-center">
                    <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-4 shrink-0">
                      <span class="text-white font-medium text-sm">2</span>
                    </div>
                    <span class="text-[#1d1d1f]">Install the application on your computer</span>
                  </li>
                  <li class="flex items-center">
                    <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-4 shrink-0">
                      <span class="text-white font-medium text-sm">3</span>
                    </div>
                    <span class="text-[#1d1d1f]">Launch Sortio and sign in or create an account</span>
                  </li>
                  <li class="flex items-center">
                    <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-4 shrink-0">
                      <span class="text-white font-medium text-sm">4</span>
                    </div>
                    <span class="text-[#1d1d1f]">Choose an AI model for file processing</span>
                  </li>
                </ol>
              </div>
            </div>
            
            <div class="md:w-1/3 shrink-0 space-y-5">
              <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
                <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Data Security
                </h4>
                <p class="text-[#424245]">By default, Sortio uses our cloud (OpenAI) and sends filenames and metadata to our servers; file contents are sent only if you enable a content-based feature. Data is encrypted in transit (TLS 1.2+) and at rest, but not end-to-end. For sensitive files, use <a href="/docs/local-models" class="text-[#06c] hover:text-[#0077e6] transition-colors font-medium">offline mode</a> (Ollama) or bring your own API key, so nothing leaves your machine.</p>
              </div>
              
              <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
                <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Backup Recommendation
                </h4>
                <p class="text-[#424245]">We strongly recommend backing up important files before sorting. While Sortio has multiple levels of redundancy to protect your data, we cannot be responsible for any data loss.</p>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "one-off-sorting",
        title: "One-Off Sorting",
        content: `
          <p class="text-[#1d1d1f] text-lg mb-6">One-off sorting is perfect for organizing a specific folder quickly:</p>
          
          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
            <div class="border-b border-[#e0e0e5] p-5">
              <h4 class="font-semibold text-[#1d1d1f]">Step-by-Step Process</h4>
            </div>
            
            <div class="p-6 space-y-7">
              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3">
                    <span class="text-white font-medium text-sm">1</span>
                  </div>
                  Select a Folder
                </h5>
                <p class="mt-2 text-[#424245] ml-10">Click "Select Folder" and choose the directory you want to organize</p>
              </div>
              
              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3">
                    <span class="text-white font-medium text-sm">2</span>
                  </div>
                  Provide a Prompt
                </h5>
                <p class="mt-2 text-[#424245] ml-10">Describe how you'd like your files organized (e.g., "Sort by project and file type" or "Organize my photos by date and location")</p>
              </div>
              
              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3">
                    <span class="text-white font-medium text-sm">3</span>
                  </div>
                  Configure Options
                </h5>
                <p class="mt-2 text-[#424245] ml-10">Choose whether Sortio should:</p>
                <ul class="mt-3 ml-10 space-y-3 text-[#424245]">
                  <li class="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Analyze file content (provides better sorting but takes longer)</span>
                  </li>
                  <li class="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Rename files (applies more descriptive names based on content)</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3">
                    <span class="text-white font-medium text-sm">4</span>
                  </div>
                  Review Sorting Plan
                </h5>
                <p class="mt-2 text-[#424245] ml-10">Sortio generates a preview of how files will be organized. You can:</p>
                <ul class="mt-3 ml-10 space-y-3 text-[#424245]">
                  <li class="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit the plan by moving files to different locations</span>
                  </li>
                  <li class="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Approve the plan to begin sorting</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h5 class="font-medium text-[#1d1d1f] flex items-center">
                  <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3">
                    <span class="text-white font-medium text-sm">5</span>
                  </div>
                  Complete the Process
                </h5>
                <p class="mt-2 text-[#424245] ml-10">After sorting, you can:</p>
                <ul class="mt-3 ml-10 space-y-3 text-[#424245]">
                  <li class="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                    </svg>
                    <span>Undo the sorting if needed</span>
                  </li>
                  <li class="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Finish and close the operation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "spaces",
        title: "Spaces",
        content: `
          <p class="text-[#1d1d1f] text-lg mb-4">Spaces are folders Sortio watches. Any file moved into a Space is automatically indexed, sorted, and linked into the Knowledge Graph.</p>
          <p class="text-[#1d1d1f] mb-6">For a full walkthrough, see the dedicated <a href="/docs/spaces" class="text-[#06c] hover:text-[#0077e6] font-medium">Spaces documentation</a>. Quick overview below:</p>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur relative overflow-hidden">
              <div class="absolute top-0 right-0 w-16 h-16">
                <div class="absolute transform rotate-45 bg-[#06c] text-white text-xs font-medium py-1 right-[-35px] top-[32px] w-[170px] text-center">
                  Set up once
                </div>
              </div>
              
              <h4 class="font-semibold text-[#1d1d1f] mb-5">Creating a Space</h4>
              
              <ol class="space-y-4">
                <li class="flex items-start">
                  <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0 mt-0.5">
                    <span class="text-white font-medium text-sm">1</span>
                  </div>
                  <div>
                    <p class="text-[#424245]">Go to <strong>Spaces</strong> in the sidebar and click <strong>Add Space</strong></p>
                  </div>
                </li>
                <li class="flex items-start">
                  <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0 mt-0.5">
                    <span class="text-white font-medium text-sm">2</span>
                  </div>
                  <div>
                    <p class="text-[#424245]">Select a location for your Space</p>
                  </div>
                </li>
                <li class="flex items-start">
                  <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0 mt-0.5">
                    <span class="text-white font-medium text-sm">3</span>
                  </div>
                  <div>
                    <p class="text-[#424245]">Set a sorting prompt and configure options</p>
                  </div>
                </li>
                <li class="flex items-start">
                  <div class="rounded-full bg-[#06c] h-7 w-7 flex items-center justify-center mr-3 shrink-0 mt-0.5">
                    <span class="text-white font-medium text-sm">4</span>
                  </div>
                  <div>
                    <p class="text-[#424245]">Save your Space configuration</p>
                  </div>
                </li>
              </ol>
            </div>
            
            <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
              <h4 class="font-semibold text-[#1d1d1f] mb-5">Using a Space</h4>
              
              <div class="space-y-6">
                <div class="bg-[#f5f5f7] rounded-xl p-4">
                  <h5 class="text-[#1d1d1f] font-medium mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Automatic Organization
                  </h5>
                  <p class="text-[#424245]">Any file moved or saved to this folder will be automatically sorted according to your rules</p>
                </div>
                
                <div>
                  <h5 class="text-[#1d1d1f] font-medium mb-3">Perfect For</h5>
                  <ul class="space-y-3">
                    <li class="flex items-start bg-white rounded-lg p-3 border border-[#e0e0e5]">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3 text-[#06c] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <span class="text-[#1d1d1f] font-medium">Download folders</span>
                        <p class="text-sm text-[#86868b] mt-1">Keep automatically organized downloads</p>
                      </div>
                    </li>
                    <li class="flex items-start bg-white rounded-lg p-3 border border-[#e0e0e5]">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3 text-[#06c] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <span class="text-[#1d1d1f] font-medium">Document collections</span>
                        <p class="text-sm text-[#86868b] mt-1">Manage growing libraries of documents</p>
                      </div>
                    </li>
                    <li class="flex items-start bg-white rounded-lg p-3 border border-[#e0e0e5]">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3 text-[#06c] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <div>
                        <span class="text-[#1d1d1f] font-medium">Project directories</span>
                        <p class="text-sm text-[#86868b] mt-1">Keep project files categorized</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
        `
      }
    ]
  };

  export default gettingStartedContent;